import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Settings } from '@stockcare-helpers/settings';
import { IPhysicalLocation } from '@stockcare-models/IPhysical-location';
import { CacheService } from '@stockcare-services/cache-service/cache.service';
import { PhysicalLocationsService } from '@stockcare-services/physical-locations-service/physical-locations.service';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { SearchService } from '@stockcare-services/search-service/search.service';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { IDetect } from '@stockcare-shared/directives/scanner-detect.directive';
import { Subject, Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-search-header',
  templateUrl: './search-header.component.html',
  styleUrls: ['./search-header.component.scss'],
})
export class SearchHeaderComponent implements OnDestroy {
  @Input() header: string = '';
  @Input() icon: string = '';
  @Input() placeholder: string = '';
  @Input() showSearch: boolean = true;
  @Input() showCreate: boolean = false;
  @Input() showHistory: boolean = false;
  @Input() autoFocus: boolean;
  @Input() isReadonly: boolean = false;
  @Input() showRefresh: boolean = true;
  @Input() showKeyboard: boolean = false;
  @Input() showClear: boolean = false;
  @Input() showClearCache: boolean = false;
  @Input() showDateFilter: boolean = false;
  @Input() disableKeyPress: boolean;
  @Input() hideHeader: boolean;
  @Input() shouldAlignIcon: boolean = false;
  @Input() showDropDownFilter: boolean = false;
  @Output() createClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() historyClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() dateFilterChanged: EventEmitter<Date> = new EventEmitter<Date>();
  @Output() inputFocused: EventEmitter<boolean> = new EventEmitter<boolean>();

  enableCustomKeyboard: boolean = false;
  
  debounce = 0;
  onScanned = new Subject();
  lastScannedEvent = new Date();
  dateFilterValue: Date = null;

  physicalLocations: IPhysicalLocation[];
  subscriptions = new Subscription();
      
  @ViewChild('searchInputField') searchInputField: ElementRef;
  keyBoardIsFocused: boolean = false;

  searchInput: string;
  @Input() set setSearchInput(value: string) {
    if (value === undefined) return;

    this.searchInput = value;
    this.searchService.onTyped(this.searchInput);
    
    if (this.searchInput == '') {
      this.searchService.clear();
    }
  };

  get getInputMode() {
    if (this.enableCustomKeyboard) {
      return 'none';
    }
    return 'text';
  }

  constructor(
    private searchService: SearchService,
    public settingsService: SettingsService,
    public cacheService: CacheService,
    private readonly deviceService: DeviceService,
    private readonly physicalLocationsService: PhysicalLocationsService
  ) {
    this.debounce = settingsService.getSettingNumber(Settings.DELAY_BETWEEN_SCANNING_IN_MILLISECONDS);
    this.subscriptions.add(
      this.searchService.onClear.subscribe(() => {
        this.clearSearch();
      })
    );

    this.subscriptions.add(this.onScanned.subscribe((value: string) => this.searchService.onScanned(value)));

    this.enableCustomKeyboard = this.settingsService.enableCustomKeyboard();
    this.getPhysicalLocations();
  }

  private getPhysicalLocations() {
    this.physicalLocationsService
      .getByInstitution(this.deviceService.getInstitutionId())
      .subscribe((physicalLocations) => {
        this.physicalLocations = physicalLocations;
      });
  }

  private clearSearch() {
    this.searchInput = '';
    if (this.searchInputField && this.searchInputField.nativeElement) {
      this.searchInputField.nativeElement.value = '';
    }
    this.searchService.onCleared();
  }

  public onDetected(event: IDetect) {
    if (event.type === 'scanner') {
      var timeSinceLastEvent = new Date().getTime() - this.lastScannedEvent.getTime();

      if (timeSinceLastEvent < this.debounce) {
        this.clearSearch();
        return;
      }

      this.lastScannedEvent = new Date();
      this.onScanned.next(event.value.toString());
    } else {
      this.searchInput = event.value.toString();
      this.searchService.onTyped(this.searchInput);
    }
  }
  
  public isInputFocused() {
    return this.keyBoardIsFocused;
  }

  clearSearchInput() {
    this.searchInput = '';
    this.searchInputField.nativeElement.value = '';
    this.searchService.clear();
    this.toggleKeyboard();
  }

  refresh() {
    this.searchService.onRefresh();
    this.dateFilterValue = null;
  }

  clearCache() {
    this.cacheService.clearCache();
  }

  onDelete() {
    if (this.searchInput && this.searchInput.length > 0) {
      this.searchInput = this.searchInput.slice(0, -1);
      this.searchService.onTyped(this.searchInput);
    } else {
      this.clearSearch();
    }
  }

  create() {
    this.createClicked.emit(true);
  }

  history() {
    this.historyClicked.next();
    return;
  }

  onDateFilterChange(event) {
    const date = new Date(event.target.value);
    this.dateFilterChanged.emit(date);
  }

  toggleKeyboard() {
    this.isReadonly = !this.isReadonly;
    this.keyBoardIsFocused = !this.keyBoardIsFocused
    this.inputFocused.emit(this.keyBoardIsFocused);
    this.focusSearchInput();
  }

  private focusSearchInput() {
    if (!this.isReadonly) {
      setTimeout(() => {
        this.searchInputField.nativeElement.focus();
      }, 0);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
